import React, { useContext, useState } from "react";
import { Button, Dropdown, Input, MenuProps, Select, Space, Table, Tooltip, Typography } from "antd";
import { CarOutlined, DownOutlined, FormOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/lib/table";
import User from "../../../models/User";
import AccountService from "../../../services/crud/AccountService";
import AddUserButton from "./AddUserButton"
import { UserGroup, GroupTypeEnum, UserGroupTableRow } from "../../../models/UserGroup";
import { useUserGroupEditModal } from "../../../hooks/userGroupEditModal";
import { OverlayContext } from "../../../context/OverlayContext";
import { ToastContext } from "../../../context/ToastContext";
import { WalletFormButton } from "../../../components/WalletFormButton";
import { Navigate, useNavigate } from "react-router-dom";
import { UserGroupContract } from "../../../models/UserGroupContract";
import moment from "moment";

const UNDEFINED = "-";
/**
 * This is a data mapper that maps the data from the API to the data that the table needs.
 */
class UserTableData extends User {
  rowKey: string;
  status: string;
  name: string;
  role: string;
  isRegistered: boolean;
  startDate: Date;
  endDate: Date;
  userGroupContractId: number|null;
  userGroupLabel: string;
  userGroup?: UserGroup;
  userGroupContract?: UserGroupContract;
  allowBookByInvoice?: boolean;
  constructor(data: User | any, ugRow?: UserGroupTableRow) {
    super(data);
    const user = data as User;
    this.rowKey = `${data.pivot.userGroupContractId}:${data.username}`;
    this.status = data?.isRegistered ? "registered" : "invited";
    this.name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    if (this.name === '') {
      this.name = '-';
    }
    console.log(data.username, 'pivot', data.pivot, ugRow);
    this.role = user.pivot.accountAdmin ? "admin" : "user";
    this.isRegistered = user.isRegistered;
    this.startDate = data.pivot.startDate;
    this.endDate = data.pivot.endDate;
    this.userGroupContractId = data.pivot.userGroupContractId ?? null;
    this.userGroupLabel = ugRow?.userGroupLabel;
    this.allowBookByInvoice = data.pivot.allowBookByInvoice;
  }
}

function UsersTable({
  dataSource = [], // array of User
  userGroups = [], // array of UserGroupTableRow
  accountId,
  onActionComplete = () => {},
  actions,
  showUserGroups = false, // bool
  isCompany = false,
  ...rest
}) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [textFilter, setTextFilter] = useState("");
  const [isRegisteredFilter, setStatusFilter] = useState<boolean|null>(null);
  const filterTable = (data) => {
    const textFilterLc = textFilter.trim().toLowerCase();
    return data.filter((item: UserTableData) => {
      // Only show direct members in users table for now
      if (item.userGroupContractId !== null) {
        //return false;
      }
      let textFilterMatch = true;
      if (textFilterLc !== '') {
        const itemData = [
          item.username,
          item.name,
          item.role,
          item.status,
          item.userGroupLabel,
        ]
        textFilterMatch = itemData
          .map((itemPart) => `${itemPart ?? ''}`.toLowerCase().indexOf(textFilterLc) !== -1)
          .reduce((any, value) => any || value, false);
      }
      const isRegisteredMatch = isRegisteredFilter === null || item.isRegistered == isRegisteredFilter;
      return textFilterMatch && isRegisteredMatch;
    });
  };
  const {openCreateUserGroup, openEditUserGroup} = useUserGroupEditModal({
    itemName: 'Apartment',
    onActionComplete,
  });
  const { loading, loadMessage } = useContext(ToastContext);
  const { openModal, closeModal } = useContext(OverlayContext);
  let navigate = useNavigate();

  const ugMap = {};
  console.log('userGroups', userGroups);
  userGroups.forEach((ugRow: UserGroupTableRow) => {
    ugMap[ugRow.userGroupContractId] = ugRow;
  });
  console.log('ugMap', ugMap);

  const dataMapper = (data) => {
    return new UserTableData(data, ugMap[data.pivot.userGroupContractId]);
  };

  const extraColumns = [];
  if (showUserGroups) {
    extraColumns.push({
      title: "Apartment",
      key: "userGroupLabel",
      dataIndex: ["userGroupLabel"],
      render: (text, record, index) => {
        if (record.userGroupContractId === null) {
          // Direct access
          return <></>
        }
        return <Typography.Text
          //style={{cursor: 'pointer'}}
          //onClick={() => openEditUserGroup(userGroup)}
        >{text}</Typography.Text>;
      },
      sorter: (a, b) => a?.userGroupLabel?.toLowerCase().localeCompare(b?.userGroupLabel?.toLowerCase()),
    });
  }
  if (isCompany) {
    extraColumns.push({
        title: "Book by invoice",
        key: "allowBookByInvoice",
        dataIndex: ["allowBookByInvoice"],
        render: (text, record, index) => {
          return <Typography.Text>{text ? 'Yes' : 'No'}</Typography.Text>;
        },
        sorter: (a, b) => `${a?.allowBookByInvoice}`.toLowerCase().localeCompare(`${b?.allowBookByInvoice}`.toLowerCase()),
    });
  }

  const columns: ColumnType<UserTableData>[] = [
    {
      title: "Username",
      key: "username", // Used by UserProfile via columnFilter prop
      dataIndex: ["username"],
      render: (text, record, index) => {
        if (actions?.viewUser) {
          return <Tooltip title="Go to users profile">
            <Typography.Text style={{ cursor: "pointer" }} onClick={() => navigate(`/users/${record.id}`)}>
              {record.username}
            </Typography.Text>
          </Tooltip>
        }
        return <Typography.Text>{text}</Typography.Text>;
      },
      sorter: (a, b) => a?.username?.toLowerCase().localeCompare(b?.username?.toLowerCase()),
    },
    {
      title: "Name",
      key: "name", // Used by UserProfile via columnFilter prop
      dataIndex: ["name"],
      render: (text, record, index) => {
        return <Typography.Text>{text ?? UNDEFINED}</Typography.Text>;
      },
      sorter: (a, b) => a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase()),
    },
    {
      title: "Role",
      key: "role", // Used by UserProfile via columnFilter prop
      dataIndex: ["role"],
      render: (text, record, index) => {
        return <Typography.Text>{text}</Typography.Text>;
      },
      sorter: (a, b) => a?.role?.toLowerCase().localeCompare(b?.role?.toLowerCase()),
    },
    {
      title: "Start date",
      key: "startDate", // Used by UserProfile via columnFilter prop
      dataIndex: ["startDate"],
      render: (text, record, index) => {
        return <>
          <Typography.Text>{record.startDate ? moment(record.startDate).format('YYYY-MM-DD') : ''}</Typography.Text>
        </>;
      },
      //sorter: (a, b) => a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase()),
    },
    {
      title: "End date",
      key: "endDate", // Used by UserProfile via columnFilter prop
      dataIndex: ["endDate"],
      render: (text, record, index) => {
        return <>
          <Typography.Text>{record.endDate ? moment(record.endDate).format('YYYY-MM-DD') : ''}</Typography.Text>
        </>;
      },
      //sorter: (a, b) => a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase()),
    },
    ...extraColumns,
    {
      title: "Last logged in",
      dataIndex: ["lastLoginAtAsDate"],
      render: (text, record, index) => {
        return <Typography.Text>{text ?? UNDEFINED}</Typography.Text>;
      },
      sorter: (a, b) => {
        // Sort down the nulls when sorting DESC
        if (a['lastLoginAt'] == null && b['lastLoginAt'] == null) return 0;
        if (a['lastLoginAt'] == null && b['lastLoginAt'] != null) return -1;
        if (a['lastLoginAt'] != null && b['lastLoginAt'] == null) return 1;
        return a['lastLoginAt'].localeCompare(b['lastLoginAt']);
      },
    },
    {
      title: "Created at",
      key: "createdAt",
      dataIndex: ["createdAtAsDate"],
      render: (text, record, index) => {
        return <Typography.Text>{text}</Typography.Text>;
      },
      sorter: (a, b) => a['createdAt'].localeCompare(b['createdAt']),
    },
    {
      title: "Status",
      key: "status", // Used by UserProfile via columnFilter prop
      dataIndex: ["status"],
      render: (text, record, index) => {
        return <Typography.Text>{text}</Typography.Text>;
      },
      sorter: (a, b) => a?.status?.toLowerCase().localeCompare(b?.status?.toLowerCase()),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record, index) => {
        return (
          <>
            {actions?.updateBalance && (
              <WalletFormButton
                buttonLabel="Wallet"
                userId={record.id}
                actorAccountId={accountId}
              />
            )}
          </>
        );
      },
    },
  ];

  const items: MenuProps["items"] = [
    { label: "Promote selected to administrator", key: "SET_SELECTED_TO_ADMIN" },
    { label: "Demote selected to user", key: "SET_SELECTED_TO_USER" },
    { dashed: false, key: 'dash1', },
    ... isCompany ? [
      { label: "Allow book by invoice", key: "SET_ALLOW_BOOK_BY_INVOICE" },
      { label: "Disallow book by invoice", key: "UNSET_ALLOW_BOOK_BY_INVOICE" },
      { dashed: false, key: 'dash2', },
    ] : [],
    {
      label: "Remove selected from account",
      key: "REMOVE_SELECTED_FROM_ACCOUNT",
    },
  ];

  const onClick = async ({ key }) => {
    const selectedRows: UserTableData[] = [];
    console.log('selected keys', selectedRowKeys);
    dataSource.forEach((row) => {
      const rowKey = `${row.pivot.userGroupContractId}:${row.username}`;
      if (selectedRowKeys.includes(rowKey)) {
        selectedRows.push(dataMapper(row));
      }
    });
    console.log('selected rows', selectedRows);

    switch (key) {
      case "REMOVE_SELECTED_FROM_ACCOUNT":
        const removeSelectFromAccount = (email, accountId, userGroupContractId) => {
          return AccountService.removeEmailFromAccount(accountId, email, userGroupContractId);
        };
        openModal({
          title: `Remove ${selectedRows.length} selected users or invited emails from account?`,
          content: <Typography.Text>Are you sure?</Typography.Text>,
          onOk: () => {
            loadMessage("Deleting item...", async () => {
              await Promise.allSettled(selectedRows.map((row) => removeSelectFromAccount(row.username, accountId, row.userGroupContractId)));
              closeModal();
              // Reload table
              onActionComplete();
            });
          },
        });
        break;
      case "SET_SELECTED_TO_ADMIN":
        const setUserToAdmin = (email, accountId, userGroupContractId) => {
          return AccountService.attachEmailToAccount(accountId, email, userGroupContractId, { accountAdmin: true });
        };
        await Promise.allSettled(selectedRows.map((row) => setUserToAdmin(row.username, accountId, row.userGroupContractId)));
        break;
      case "SET_SELECTED_TO_USER":
        const setUserToUser = (email, accountId, userGroupContractId) => {
          return AccountService.attachEmailToAccount(accountId, email, userGroupContractId, { accountAdmin: false });
        };
        await Promise.allSettled(selectedRows.map((row) => setUserToUser(row.username, accountId, row.userGroupContractId)));
        break;
      case "SET_ALLOW_BOOK_BY_INVOICE":
        const setAllowBookByInvoice = (email, accountId, userGroupContractId) => {
          return AccountService.attachEmailToAccount(accountId, email, userGroupContractId, { allowBookByInvoice: true });
        };
        await Promise.allSettled(selectedRows.map((row) => setAllowBookByInvoice(row.username, accountId, row.userGroupContractId)));
        break;
      case "UNSET_ALLOW_BOOK_BY_INVOICE":
        const unsetAllowBookByInvoice = (email, accountId, userGroupContractId) => {
          return AccountService.attachEmailToAccount(accountId, email, userGroupContractId, { allowBookByInvoice: false });
        };
        await Promise.allSettled(selectedRows.map((row) => unsetAllowBookByInvoice(row.username, accountId, row.userGroupContractId)));
        break;
    }
    // Reload table
    if (key != "REMOVE_SELECTED_FROM_ACCOUNT") {
      onActionComplete();
    }
  };

  type ActionDropDownProps = {};
  const ActionsDropdown: React.FC<ActionDropDownProps> = ({}) => (
    <Dropdown menu={{ items, onClick }} trigger={["click"]}>
      <a>
        <Space>
          Actions
          <DownOutlined />
        </Space>
      </a>
    </Dropdown>
  );

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <>
      <Typography.Title level={3}>Users</Typography.Title>
      <Space style={{ marginBottom: 10, justifyContent: "space-between", width: "100%" }}>
        <Space>
          <ActionsDropdown />
          <span style={{ marginLeft: 8 }}> {`(Selected ${selectedRowKeys.length} items)`} </span>
          <Input.Search size="middle" placeholder={`Search Users ...`} style={{ width: 200, marginLeft: 30 }} onChange={(e) => setTextFilter(e.target.value)} />
          <Select
            allowClear
            defaultValue={[]}
            options={[{value: true, label: 'registered'}, {value: false, label: 'invited'}]}
            onChange={(value) => setStatusFilter((value ?? null) as any)}
            placeholder="Status"
            suffixIcon={<FormOutlined/>}
            style={{width: 200}}
          />
        </Space>
        <AddUserButton
          accountId={accountId}
          onSuccess={() => onActionComplete()}
        ></AddUserButton>
      </Space>
      <Table
        bordered
        rowSelection={rowSelection}
        rowKey="rowKey"
        dataSource={filterTable(dataSource.map(dataMapper))}
        columns={columns}
        pagination={{ total: dataSource?.length, defaultPageSize: 30, showSizeChanger: true }}
        {...rest}
      ></Table>
    </>
  );
}

export default UsersTable;
